import { useCallback, useMemo, useRef, useState } from 'react';
import { FieldValueOption, ParentFieldEntry } from '@aidkitorg/types/lib/admin-panel/settings';
import { usePost } from '../../../API';
import { useSettings } from '../../../Contexts/SettingsContext';

const labelKey = (field: string, value: string) => `${field}\u0000${value}`;

export const useFieldValueSuggestions = () => {
  const fetchFieldValuesAPI = usePost('/api/admin_panel/field_values');
  const fetchAPIRef = useRef(fetchFieldValuesAPI);

  // Keep ref updated but don't trigger re-memoization
  fetchAPIRef.current = fetchFieldValuesAPI;

  const { fields } = useSettings();
  const userFields = useMemo(() => new Set(
    fields.filter(f => !f.parent && (f as ParentFieldEntry).dataType === 'user').map(f => f.id)
  ), [fields]);

  // Labels for values whose stored form isn't readable (a user field's uid -> the user's name),
  // learned from suggestion responses so filter badges can show them.
  const [labels, setLabels] = useState<Record<string, string>>({});
  const requestedRef = useRef(new Set<string>());

  const fetchSuggestions = useCallback(async (field: string, search: string): Promise<string[] | FieldValueOption[]> => {
    try {
      const response = await fetchAPIRef.current({
        field,
        search: search || undefined,
        limit: 20
      });

      if (response.success && response.data) {
        const values: string[] | FieldValueOption[] = response.data.values || [];
        const options = (values as Array<string | FieldValueOption>).filter((v): v is FieldValueOption => typeof v !== 'string');
        if (options.length > 0) {
          setLabels(prev => {
            const next = { ...prev };
            options.forEach(o => { next[labelKey(field, o.value)] = o.label; });
            return next;
          });
        }
        return values;
      }

      return [];
    } catch (err) {
      console.error('Error fetching field value suggestions:', err);
      return [];
    }
  }, []);

  // Badges render before any suggestions are fetched (e.g. a saved filter), so an unknown
  // user-field value is looked up once, outside of render, by searching for its uid.
  const formatFilterValue = useCallback((field: string, value: string): string | undefined => {
    const key = labelKey(field, value);
    const label = labels[key];
    if (label !== undefined || !userFields.has(field) || !value || requestedRef.current.has(key)) return label;
    requestedRef.current.add(key);
    queueMicrotask(() => { void fetchSuggestions(field, value); });
    return undefined;
  }, [labels, userFields, fetchSuggestions]);

  return {
    fetchSuggestions,
    formatFilterValue
  };
};
